import type { SidekickActingOrganizationScope } from '../api/actingOrganization';
import { fetchWithSidekickAuth } from '../sse/sidekickHttp';
import type { TableData, TableRowValue } from '../structuredQuery/types';

const CSV_MIME_TYPE = 'text/csv;charset=utf-8';
const CSV_EXTENSION = '.csv';
const UTF_8_BOM = '\uFEFF';
const MAX_FILENAME_BASE_LENGTH = 120;
const PARTIAL_FILENAME_SUFFIX = '-partial';

type ChartDownloadArgs = {
  url: string;
};

type TableDownloadArgs = {
  tableData: TableData;
  title?: string;
};

const isNumericString = (value: string): boolean =>
  /^-\d+(?:\.\d+)?$/.test(value);

const needsFormulaNeutralization = (value: string): boolean => {
  if (value === '') {
    return false;
  }

  const normalized = value.trimStart();
  const firstCharacter = normalized[0];

  return (
    firstCharacter === '=' ||
    firstCharacter === '+' ||
    firstCharacter === '@' ||
    (firstCharacter === '-' && !isNumericString(normalized))
  );
};

const escapeCsvValue = (value: TableRowValue | undefined): string => {
  if (value === null || value === undefined) {
    return '';
  }

  const safeValue = needsFormulaNeutralization(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safeValue)
    ? `"${safeValue.replaceAll('"', '""')}"`
    : safeValue;
};

const replaceUnsafeFilenameCharacters = (value: string): string =>
  Array.from(value, (character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 31 || '<>:"/\\|?*'.includes(character)
      ? '-'
      : character;
  }).join('');

const sanitizeFilenameBase = (value: string): string =>
  replaceUnsafeFilenameCharacters(value.normalize('NFKC'))
    .replaceAll(/\s+/g, ' ')
    .replaceAll(/^[.\s]+|[.\s]+$/g, '')
    .slice(0, MAX_FILENAME_BASE_LENGTH);

const withExtension = (
  base: string,
  extension: string,
  suffix = ''
): string => {
  const extensionPattern = new RegExp(`${extension.replace('.', '\\.')}$`, 'i');
  return `${base.replace(extensionPattern, '')}${suffix}${extension}`;
};

const assertSecureChartUrl = (url: string): URL => {
  const parsedUrl = new URL(url);
  if (
    parsedUrl.protocol !== 'https:' ||
    parsedUrl.username !== '' ||
    parsedUrl.password !== ''
  ) {
    throw new Error('Sidekick chart downloads require a secure URL');
  }

  return parsedUrl;
};

/**
 * Serializes the backend-issued table payload in its original column and row
 * order. Formula-like strings are prefixed with an apostrophe so spreadsheet
 * applications display them as text instead of executing them.
 */
export const serializeTableDataToCsv = (tableData: TableData): string => {
  const header = tableData.columns
    .map((column) => escapeCsvValue(column.name))
    .join(',');
  const rows = tableData.rows.map((row) =>
    tableData.columns
      .map((_, columnIndex) => escapeCsvValue(row[columnIndex]))
      .join(',')
  );

  return [header, ...rows].join('\r\n');
};

/** Resolves a safe CSV filename and marks truncated payloads as partial. */
export const getTableDownloadFilename = (
  title?: string,
  isPartial = false
): string => {
  const titleBase = sanitizeFilenameBase(title ?? '');
  return withExtension(
    titleBase || 'sidekick-table',
    CSV_EXTENSION,
    isPartial ? PARTIAL_FILENAME_SUFFIX : ''
  );
};

/**
 * Triggers a browser download and releases the temporary object URL even when
 * clicking the generated link throws.
 */
export const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;

  try {
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
  }
};

/** Arguments for {@link downloadArtifact}. */
export type ArtifactDownloadArgs = {
  /** Sidekick API base URL from the host. */
  apiBaseUrl: string;
  conversationId: string;
  artifactId: string;
  /** Filename to use when the response names none. */
  fallbackFilename: string;
  /** The organization the download was clicked in. Defaults to the current. */
  organizationScope?: SidekickActingOrganizationScope;
};

// The encoded `filename*` wins over plain `filename` when a header has both.
const CONTENT_DISPOSITION_ENCODED_FILENAME_PATTERN =
  /filename\*=(?:UTF-8'')?([^;]+)/i;
const CONTENT_DISPOSITION_FILENAME_PATTERN = /filename="?([^";]+)"?/i;

/** Reads a safe filename from a `Content-Disposition` header, if it has one. */
const getContentDispositionFilename = (
  header: string | null
): string | undefined => {
  const raw =
    header?.match(CONTENT_DISPOSITION_ENCODED_FILENAME_PATTERN)?.[1] ??
    header?.match(CONTENT_DISPOSITION_FILENAME_PATTERN)?.[1];
  if (raw === undefined) {
    return undefined;
  }
  let decoded = raw.trim();
  try {
    decoded = decodeURIComponent(decoded);
  } catch {
    // Keep the raw value when it is not percent-encoded.
  }
  const safe = replaceUnsafeFilenameCharacters(decoded).trim();
  return safe === '' ? undefined : safe;
};

/**
 * How long a chart download waits to find its saved artifact. Its fallback
 * opens the image in a new tab, which browsers only allow for a few seconds
 * after the click, so the lookup can't hold it up for longer.
 */
export const CHART_ARTIFACT_LOOKUP_TIMEOUT_MS = 3000;

/**
 * Resolves what `pending` resolves, or `undefined` once `timeoutMs` pass
 * first.
 * @param pending - The lookup to wait on, if any.
 * @param timeoutMs - How long to wait.
 * @returns The lookup's value, or `undefined` when it took too long.
 */
export const settleWithin = async <T>(
  pending: Promise<T> | undefined,
  timeoutMs: number
): Promise<T | undefined> => {
  if (pending === undefined) {
    return undefined;
  }
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>((resolve) => {
    timeoutId = setTimeout(() => resolve(undefined), timeoutMs);
  });
  try {
    return await Promise.race([pending, timeout]);
  } finally {
    clearTimeout(timeoutId);
  }
};

/** Resolves a safe PNG filename for a chart download. */
export const getChartDownloadFilename = (title?: string): string =>
  withExtension(sanitizeFilenameBase(title ?? '') || 'sidekick-chart', '.png');

/**
 * Downloads a table or chart through the backend artifact endpoint, which
 * serves the full table as CSV or the chart as PNG from the Sidekick origin.
 * Rejects when the request fails, so the caller can show its error state.
 */
export const downloadArtifact = async ({
  apiBaseUrl,
  conversationId,
  artifactId,
  fallbackFilename,
  organizationScope,
}: ArtifactDownloadArgs): Promise<void> => {
  const response = await fetchWithSidekickAuth({
    organizationScope,
    url: `${apiBaseUrl}/conversations/${encodeURIComponent(
      conversationId
    )}/artifacts/${encodeURIComponent(artifactId)}/download`,
    signal: new AbortController().signal,
    init: { method: 'GET' },
  });
  const blob = await response.blob();
  triggerBlobDownload(
    blob,
    getContentDispositionFilename(
      response.headers.get('Content-Disposition')
    ) ?? fallbackFilename
  );
};

/**
 * Opens a secure chart URL without fetching it. The browser handles the
 * response directly, avoiding a cross-origin read from the CDN.
 */
export const downloadChartImage = ({ url }: ChartDownloadArgs): void => {
  const parsedUrl = assertSecureChartUrl(url);
  const link = document.createElement('a');
  link.href = parsedUrl.toString();
  link.referrerPolicy = 'no-referrer';
  link.target = '_blank';
  link.rel = 'noopener noreferrer';

  try {
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
  }
};

/** Serializes and downloads every row present in a Sidekick table payload. */
export const downloadTableCsv = ({
  tableData,
  title,
}: TableDownloadArgs): void => {
  const csv = serializeTableDataToCsv(tableData);
  const blob = new Blob([UTF_8_BOM, csv], { type: CSV_MIME_TYPE });
  triggerBlobDownload(
    blob,
    getTableDownloadFilename(title ?? tableData.title, tableData.truncated)
  );
};
